/**
 * Autopilot — main hub. Lists user's sessions; CTA to launch wizard for new session.
 */
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import {
  Plus,
  Activity,
  Pause,
  Coffee,
  Square,
  Trash2,
  Workflow,
  MessageSquare,
} from "lucide-react";
import { useAutopilotSessions, useDeleteAutopilotSession } from "@/hooks/useAutopilot";
import { useToast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { FeedbackModal } from "@/components/autopilot/FeedbackModal";
import type { AutopilotSession } from "@shared/schema";

const STATUS_BADGE: Record<string, { color: string; icon: typeof Activity; label: string }> = {
  active: { color: "bg-green-500", icon: Activity, label: "Active" },
  paused: { color: "bg-yellow-500", icon: Pause, label: "Paused" },
  vacation: { color: "bg-blue-500", icon: Coffee, label: "Vacation" },
  stopped: { color: "bg-gray-500", icon: Square, label: "Stopped" },
  draft: { color: "bg-gray-300", icon: Plus, label: "Draft" },
};

const FUNNEL_LABEL: Record<string, string> = {
  ecom: "E-commerce (Theriot)",
  lead_gen: "Lead Gen (Haynes)",
  book_call: "Book a Call (Haynes)",
  trial_saas: "Trial SaaS",
  direct_sale: "Direct Sale",
  observer: "Observer (fallback)",
};

export default function AutopilotHub() {
  const { data: sessions, isLoading } = useAutopilotSessions();
  const deleteSession = useDeleteAutopilotSession();
  const { toast } = useToast();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleDelete = (e: React.MouseEvent, sessionId: string, label: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      !confirm(
        `Usunąć sesję "${label}"? Tej operacji nie da się cofnąć — kasują się wszystkie decyzje, alerty i snapshoty KPI.`
      )
    )
      return;
    deleteSession.mutate(sessionId, {
      onSuccess: () => toast({ title: "Sesja usunięta" }),
      onError: (err) =>
        toast({
          title: "Nie udało się usunąć sesji",
          description: FriendlyErrorMapper.map(err).description,
          variant: "destructive",
        }),
    });
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">Autopilot reklam</h1>
            <p className="text-sm text-muted-foreground">
              Klikasz 1 guzik. Autopilot prowadzi kampanie do KPI w metodologii Theriota / Haynesa.
              <span className="font-semibold"> Bez Meta defaults.</span>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="lg"
              variant="outline"
              onClick={() => setFeedbackOpen(true)}
              className="gap-2 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              title="Wewnętrzne narzędzie testerów: zostaw feedback, zgłoś pomysł lub bug"
            >
              <MessageSquare className="w-4 h-4" />
              Feedback / Pomysły
            </Button>
            <Link href="/admin/autopilot/pipeline">
              <Button size="lg" variant="outline" className="gap-2">
                <Workflow className="w-4 h-4" />
                Pipeline kreatywny
              </Button>
            </Link>
            <Link href="/admin/autopilot/new">
              <Button size="lg" className="gap-2">
                <Plus className="w-4 h-4" />
                Nowa sesja
              </Button>
            </Link>
          </div>
        </div>

        <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

        {isLoading && (
          <Card>
            <CardContent className="p-6 text-muted-foreground">Ładowanie sesji…</CardContent>
          </Card>
        )}

        {!isLoading && (sessions ?? []).length === 0 && (
          <Card>
            <CardContent className="p-10 text-center">
              <p className="text-muted-foreground mb-4">Brak aktywnych sesji autopilota.</p>
              <Link href="/admin/autopilot/new">
                <Button>Rozpocznij wizard onboardingu</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(sessions ?? []).map((s: AutopilotSession) => {
            const badge = STATUS_BADGE[s.status] ?? STATUS_BADGE.draft;
            const StatusIcon = badge.icon;
            const funnelLabel = FUNNEL_LABEL[s.funnelType] ?? s.funnelType;
            return (
              <Card key={s.id} className="hover:shadow-md transition relative">
                <Link href={`/admin/autopilot/${s.id}`}>
                  <div className="cursor-pointer">
                    <CardHeader>
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-lg">{funnelLabel}</CardTitle>
                        <Badge className={`${badge.color} text-white gap-1 shrink-0`}>
                          <StatusIcon className="w-3 h-3" />
                          {badge.label}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm space-y-1">
                        <div>
                          <span className="text-muted-foreground">Branża:</span> {s.industry ?? "—"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Rynek:</span> {s.market ?? "—"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Tryb:</span> {s.mode}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Daily max:</span>{" "}
                          {s.dailyBudgetMax ?? "—"}
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Link>
                <div className="px-6 pb-4 pt-0 flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => handleDelete(e, s.id, funnelLabel)}
                    disabled={deleteSession.isPending}
                    className="gap-2 text-red-400 border-red-500/30 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Usuń sesję
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
